/**
 * Table atoms — shadcn/Radix-aligned with Spectrum extras.
 * Anatomy: Table, TableCaption, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell.
 * Use TableCaption for accessible captions, TableFooter for totals.
 * Row variants: standard (white), quiet (gray). Cell: align, numeric (right-align + tabular-nums).
 * @see https://ui.shadcn.com/docs/components/radix/table
 */

import * as React from "react";
import { twMerge } from "tailwind-merge";
import { cn } from "@/utils/cn";
import {
  getTableRowClasses,
  getTableCellClasses,
  getTableHeadClasses,
} from "./Table.styles";
import { getTableWrapperProps } from "./Table.utils";
import type {
  TableProps,
  TableRowProps,
  TableHeadProps,
  TableCellProps,
} from "./Table.types";

export type { TableVariant, TableCellAlign } from "./Table.types";

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, scrollX, scrollY, hideScrollbar, containerClassName, ...props }, ref) => {
    const { wrapperClassName } = getTableWrapperProps({
      scrollX,
      scrollY,
      hideScrollbar,
    });

    return (
      <div data-slot="table-container" className={cn(wrapperClassName, containerClassName)}>
        <table
          data-slot="table"
          ref={ref}
          className={cn("w-full caption-bottom text-sm border-collapse", className)}
          {...props}
        />
      </div>
    );
  }
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={twMerge(
      "[&_tr]:border-b [&_tr]:border-light-lila/40",
      className
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-light-lila/40 bg-background-gray font-body font-semibold [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("my-2 text-center font-body text-xs text-muted-foreground tracking-wider", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, variant = "standard", inHeader = false, ...props }, ref) => (
    <tr
      ref={ref}
      className={getTableRowClasses(variant, inHeader, className)}
      {...props}
    />
  )
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align = "left", ...props }, ref) => (
    <th
      ref={ref}
      className={getTableHeadClasses(align, className)}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align = "left", numeric = false, ...props }, ref) => (
    <td
      ref={ref}
      className={getTableCellClasses(align, numeric, className)}
      {...props}
    />
  )
);
TableCell.displayName = "TableCell";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableCaption,
  TableRow,
  TableHead,
  TableCell,
};
